import { SafariViewController } from '@pdcshell/helpers';
import { coerceToError, OpenIdError, type PdcError } from '../error';
import { addWindowListener } from '@pdcfrontendui/utils';
import { z } from 'zod';
import { decodeSearchParams, encodeSearchParams } from '../url';
import { GlobalSettings } from '../generated/api/Common_api';
import { isCordovaApp } from '@pdcshell/helpers/utils';

export type OpenIDConnectConfig<T = never> = {
  authorizeUrl: string;
  clientId: string;
  scope: string;
  redirectUri: string;
  resource: string;
  acrValues: string;
  /**
   * Was there an error in the previous authentication attempt?
   * If so, the authorization request will include a prompt to force re-authentication.
   */
  forceReAuthentication?: boolean;
  state?: T;
};

export function openIDConfigFromGlobalSettings(
  globalSettings: GlobalSettings,
  planner: boolean,
  forceReAuthentication?: boolean,
  redirectUriSuffix?: string
): OpenIDConnectConfig {
  return {
    authorizeUrl: globalSettings.openIdAuthorizationEndpoint ?? '',
    clientId: globalSettings.openIdClientId ?? '',
    redirectUri: `${
      planner
        ? (globalSettings.openIdRedirectUriPlanner ?? '')
        : (globalSettings.openIdRedirectUriEmployee ?? '')
    }${redirectUriSuffix ?? ''}`,
    resource: globalSettings.openIdResource ?? '',
    scope: planner
      ? (globalSettings.openIdScopePlanner ?? '')
      : (globalSettings.openIdScopeEmployee ?? ''),
    acrValues: planner
      ? (globalSettings.openIdAcrValuesPlanner ?? '')
      : (globalSettings.openIdAcrValuesEmployee ?? ''),
    forceReAuthentication,
  };
}

export type OpenIDParams = {
  /** Frontend only ever calls with "authorization_code" */
  grant_type: 'authorization_code';
  client_id: string;
  scope: string;
  /** Always "code" */
  response_type: 'code';
  redirect_uri: string;
  resource: string;
  acr_values: string;
  'client-request-id': string;
  prompt?: 'none' | 'login' | 'consent' | 'select_account' | 'create';
  state?: string;
};

function makeAuthorizeRequestUrl<T = never>(conf: OpenIDConnectConfig<T>) {
  const authUrl = conf.authorizeUrl.replace(/[?/]+$/, '');
  const paramsObj: OpenIDParams = {
    client_id: conf.clientId,
    response_type: 'code',
    grant_type: 'authorization_code',
    redirect_uri: conf.redirectUri,
    resource: conf.resource,
    scope: conf.scope,
    acr_values: conf.acrValues,
    'client-request-id': crypto.randomUUID(),
    state: conf.state ? window.btoa(JSON.stringify(conf.state)) : undefined,
    prompt: conf.forceReAuthentication ? 'login' : undefined,
  };

  return `${authUrl}?${encodeSearchParams(paramsObj)}`;
}

const getCanUseSafariViewBrowser = async (
  onError?: (error: PdcError) => void
) =>
  await SafariViewController.isAvailable().catch((e: unknown) => {
    onError?.(coerceToError(e));
    return false;
  });

const codeSchema = z.object({
  code: z.string().min(1),
});

const codeWithStateSchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1).optional(),
});

const errorSchema = z.object({
  error: z.string(),
  error_description: z.string().optional(),
});

const redirectDataSchema = z.object({
  type: z.literal('OPENID_CALLBACK'),
  parameters: z.string(),
});

const SESSION_KEY_IN_PROGRESS = 'openIdLoginInProgress'; // Arbitrary string
const SESSION_KEY_STATE = 'openIdLoginState'; // Arbitrary string

export async function initiateOpenIDAuthFlow<T>(
  config: OpenIDConnectConfig<T>,
  sameWindow: boolean,
  logError?: (error: PdcError) => void
): Promise<string> {
  const canUseSafariViewBrowser =
    isCordovaApp() && (await getCanUseSafariViewBrowser(logError));
  const authorizeUrl = makeAuthorizeRequestUrl(config);

  if (!isCordovaApp() && sameWindow) {
    // Mark that we are starting an OpenID login
    sessionStorage.setItem(SESSION_KEY_IN_PROGRESS, 'true');
    if (config.state) {
      sessionStorage.setItem(
        SESSION_KEY_STATE,
        window.btoa(JSON.stringify(config.state))
      );
    }
    window.location.href = authorizeUrl;
    return Promise.resolve(''); // Will not be reached
  }

  if (canUseSafariViewBrowser) {
    try {
      await SafariViewController.show({
        url: encodeURI(authorizeUrl),
        hidden: false,
        animated: false,
        transition: 'curl',
        enterReaderModeIfAvailable: false,
      });
      return await Promise.resolve(''); // Will not be reached
    } catch (error) {
      if (error instanceof Error) {
        throw new OpenIdError({
          type: 'safariViewControllerUnavailable',
          message: error.message + ' happened on SafariViewController.show',
        });
      }
    }
  }

  // Create a promise that resolves when auth completes
  const promise = new Promise<string>((resolve, reject) => {
    const onExit = () => {
      reject(new OpenIdError({ type: 'externalWindowClosed' }));
    };

    if (window.cordova) {
      const newWindow = window.cordova.InAppBrowser.open(
        authorizeUrl,
        'openid',
        'location=yes,usewkwebview=yes'
      );

      newWindow.addEventListener('exit', onExit);

      const onLoadStart = (event: Event & { url: string }) => {
        if (
          event.url.toLowerCase().startsWith(config.redirectUri.toLowerCase())
        ) {
          newWindow.removeEventListener('loadstart', onLoadStart);
          newWindow.close();
          resolve(new URL(event.url).search);
        }
      };

      try {
        newWindow.addEventListener('loadstart', onLoadStart);
      } catch {
        // Ignore possible security error in browsers
      }
    } else {
      let newWindow: Window | null = null;
      // Browser: Use message event (postMessage pattern)
      // The redirect page must execute this on load:
      // window.opener.postMessage({ type: 'OPENID_CALLBACK', parameters: window.location.search }, window.location.origin);
      const cleanup = addWindowListener('message', (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        const parsed = redirectDataSchema.safeParse(event.data);
        if (parsed.success) {
          resolve(parsed.data.parameters);
        } else {
          reject(
            new OpenIdError({
              type: 'invalidResponse',
              userOnlyMessage: parsed.error.message,
            })
          );
        }
        cleanup();
        newWindow?.close();
      });

      newWindow = window.open(authorizeUrl, 'openid', 'location=yes');
      if (newWindow) {
        newWindow.addEventListener('exit', onExit);
      } else {
        cleanup();
        reject(new OpenIdError({ type: 'openWindowFailed' }));
      }
    }
  });

  const parameters = await promise;
  const parsed = decodeSearchParams(codeSchema, { search: parameters });
  if (parsed.code) {
    return parsed.code;
  } else {
    const parsedError = decodeSearchParams(errorSchema, { search: parameters });
    throw new OpenIdError({
      type: 'missingCodeParameter',
      userOnlyMessage:
        parsedError.error_description ?? parsedError.error ?? parameters,
    });
  }
}

export type OpenIDRedirectState<T> =
  | {
      /**
       * The user was redirected back to the app successfully
       */
      type: 'redirected';
      /**
       * Never empty
       */
      code: string;
      /**
       * Any state passed in the initial request, or null if none or invalid
       */
      state: T | null;
    }
  | {
      /**
       * The user was redirected back to the app, but there was an error
       */
      type: 'redirectedWithError';
      /**
       * NOTE: This error might contain sensitive information
       */
      error: OpenIdError;
    }
  | {
      /**
       * The user was not redirected back to the app.
       * Probably they have not initiated an OpenID login flow.
       */
      type: 'notRedirected';
    };

export function parseOpenIDRedirectState<T>(
  stateSchema: z.ZodType<T>,
  location: { search: string } = window.location
): OpenIDRedirectState<T> {
  const isRedirected = !!sessionStorage.getItem(SESSION_KEY_IN_PROGRESS);
  if (isRedirected) {
    sessionStorage.removeItem(SESSION_KEY_IN_PROGRESS);
    const params = decodeSearchParams(codeWithStateSchema, location);
    if (!params.code) {
      const error = decodeSearchParams(errorSchema, location);
      return {
        type: 'redirectedWithError',
        error: new OpenIdError({
          type: 'missingCodeParameter',
          userOnlyMessage: error.error_description ?? error.error,
        }),
      };
    }
    let state = params.state ?? null;
    if (!state) {
      // Try fallback to session storage
      state = sessionStorage.getItem(SESSION_KEY_STATE);
      if (state) {
        sessionStorage.removeItem(SESSION_KEY_STATE);
      }
    }
    if (state !== null) {
      const parsed: z.ZodSafeParseResult<T> = stateSchema.safeParse(
        JSON.parse(window.atob(state))
      );
      if (parsed.success) {
        return {
          type: 'redirected',
          code: params.code,
          state: parsed.data,
        };
      }
    }
    return {
      type: 'redirected',
      code: params.code,
      state: null,
    };
  }
  return { type: 'notRedirected' };
}

/**
 * Clear OpenID redirect parameters from the URL
 */
export function clearOpenIDRedirectState() {
  const params = new URLSearchParams(window.location.search);
  params.delete('code' satisfies keyof z.infer<typeof codeSchema>);
  params.delete('state' satisfies keyof z.infer<typeof codeWithStateSchema>);
  const encoded = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${encoded ? `?${encoded}` : ''}${window.location.hash}`
  );
}
